import React, { useMemo, useState } from 'react';
import { LoadingSpinner, SearchBar, Spacer } from '@tw/tw-component-library';
import { Button } from '@/ui';
import {
  FiBriefcase,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiFolder,
  FiMessageSquare,
  FiSearch,
} from 'react-icons/fi';
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useUserRecentAuditActivity } from '@/hooks/workspaceDashboard/useUserRecentAuditActivity';
import {
  RECENT_ACTIVITY_ACTION_OPTIONS,
  RECENT_ACTIVITY_ENTITY_OPTIONS,
  RecentActivityActionFilter,
  RecentActivityDisplayItem,
  RecentActivityEntityFilter,
} from '@/utils/userAuditLog/recentActivity';
import {
  ActivityIcon,
  ActivityItemMain,
  ActivityItemMeta,
  ActivityItemTitle,
  ActivityLeft,
  ActivityList,
  ActivityRow,
  ActivityTimestamp,
  ActivityTitle,
  ActivityTitleRow,
  Container,
  EmptyState,
  PaginationControls,
  PaginationInfo,
} from '../CompactWorkspaceDirectoryList/styles';
import {
  CompactFilterChip,
  CompactFilterLabel,
  CompactFilterRow,
  FiltersBody,
  FiltersChevron,
  FiltersDetails,
  FiltersSummary,
  FiltersSummaryText,
} from './styles';

export type CompactRecentActivityListProps = {
  title?: string;
  onNavigate?: (route: string) => void;
};

const getActivityIcon = (item: RecentActivityDisplayItem) => {
  const entity = item.recentActivityEntityType?.toLowerCase() ?? '';
  if (entity.includes('ask')) return <FiMessageSquare />;
  if (entity.includes('file') || entity.includes('document')) {
    return <FiFileText />;
  }
  if (entity.includes('collection')) return <FiFolder />;
  if (item.recentActivityEntityType === 'Workspace') {
    return <FiBriefcase />;
  }
  return <FiBriefcase />;
};

const formatActionChipLabel = (value: RecentActivityActionFilter) =>
  value === 'all' ? 'All' : value;

const formatEntityChipLabel = (value: RecentActivityEntityFilter) => {
  if (value === 'all') return 'All';
  if (value === 'AskSession') return 'Ask';
  if (value === 'DataRoomFile') return 'Docs';
  if (value === 'DataRoomCollection') return 'Collections';
  if (value === 'Workflow') return 'Workflow';
  return value;
};

const formatActionSummaryLabel = (value: RecentActivityActionFilter) =>
  value === 'all' ? '' : value;

const formatEntitySummaryLabel = (value: RecentActivityEntityFilter) => {
  if (value === 'all') return '';
  if (value === 'AskSession') return 'Ask';
  if (value === 'DataRoomFile') return 'Documents';
  if (value === 'DataRoomCollection') return 'Collections';
  return value;
};

export const CompactRecentActivityList: React.FC<
  CompactRecentActivityListProps
> = ({ title = 'Recent activity', onNavigate }) => {
  const { getAccessToken, currentUser } = UseAuth();
  const { workspaces } = useWorkspace();
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] =
    useState<RecentActivityActionFilter>('all');
  const [entityFilter, setEntityFilter] =
    useState<RecentActivityEntityFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const userId = currentUser?.userId ?? '';

  const { items, isLoading, error, hasMore } = useUserRecentAuditActivity({
    enabled: Boolean(userId),
    getAccessToken,
    userId,
    workspaces,
    page,
    actionFilter,
    entityFilter,
  });

  const filteredItems = searchTerm.trim()
    ? items.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.metaLine.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : items;

  const handleItemClick = (item: RecentActivityDisplayItem) => {
    if (item.route) {
      onNavigate?.(item.route);
    }
  };

  const resetFilters = () => {
    setPage(0);
  };

  const filtersSummary = useMemo(() => {
    const active: string[] = [];
    const action = formatActionSummaryLabel(actionFilter);
    const entity = formatEntitySummaryLabel(entityFilter);
    if (action) active.push(action);
    if (entity) active.push(entity);
    return active.length ? active.join(' · ') : null;
  }, [actionFilter, entityFilter]);

  return (
    <Container>
      <ActivityTitle>{title}</ActivityTitle>

      <SearchBar
        searchTerm={searchTerm}
        placeholder="Search activity..."
        autoComplete="off"
        spellCheck={false}
        setSearchTerm={setSearchTerm}
        leftIcon={<FiSearch />}
      />
      <Spacer />

      <FiltersDetails>
        <FiltersSummary>
          <FiltersSummaryText>
            Filters
            {filtersSummary ? ` · ${filtersSummary}` : ''}
          </FiltersSummaryText>
          <FiltersChevron aria-hidden>
            <FiChevronDown size={14} />
          </FiltersChevron>
        </FiltersSummary>
        <FiltersBody>
          <CompactFilterRow role="tablist" aria-label="Filter by action">
            <CompactFilterLabel>Action</CompactFilterLabel>
            {RECENT_ACTIVITY_ACTION_OPTIONS.map((option) => (
              <CompactFilterChip
                key={option}
                type="button"
                role="tab"
                aria-selected={actionFilter === option}
                $isActive={actionFilter === option}
                onClick={() => {
                  setActionFilter(option);
                  resetFilters();
                }}
              >
                {formatActionChipLabel(option)}
              </CompactFilterChip>
            ))}
          </CompactFilterRow>
          <CompactFilterRow role="tablist" aria-label="Filter by type">
            <CompactFilterLabel>Type</CompactFilterLabel>
            {RECENT_ACTIVITY_ENTITY_OPTIONS.map((option) => (
              <CompactFilterChip
                key={option}
                type="button"
                role="tab"
                aria-selected={entityFilter === option}
                $isActive={entityFilter === option}
                onClick={() => {
                  setEntityFilter(option);
                  resetFilters();
                }}
              >
                {formatEntityChipLabel(option)}
              </CompactFilterChip>
            ))}
          </CompactFilterRow>
        </FiltersBody>
      </FiltersDetails>

      {isLoading ? (
        <LoadingSpinner size="small" />
      ) : error ? (
        <EmptyState>{error}</EmptyState>
      ) : filteredItems.length > 0 ? (
        <ActivityList>
          {filteredItems.map((item) => (
            <ActivityRow key={item.id}>
              <ActivityLeft
                type="button"
                onClick={() => handleItemClick(item)}
                disabled={!item.route}
              >
                <ActivityIcon>{getActivityIcon(item)}</ActivityIcon>
                <ActivityItemMain>
                  <ActivityTitleRow>
                    <ActivityItemTitle>{item.title}</ActivityItemTitle>
                    <ActivityTimestamp>
                      {item.recentActivityTimestamp}
                    </ActivityTimestamp>
                  </ActivityTitleRow>
                  <ActivityItemMeta>{item.metaLine}</ActivityItemMeta>
                </ActivityItemMain>
              </ActivityLeft>
            </ActivityRow>
          ))}
        </ActivityList>
      ) : (
        <EmptyState>No recent activity found for these filters.</EmptyState>
      )}

      {!isLoading && !error && (
        <PaginationControls>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || isLoading}
            leftIcon={<FiChevronLeft />}
            aria-label="Previous page"
          />
          <PaginationInfo>Page {page + 1}</PaginationInfo>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || isLoading}
            rightIcon={<FiChevronRight />}
            aria-label="Next page"
          />
        </PaginationControls>
      )}
    </Container>
  );
};
